import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AIChatbotSettingsTabs from "./AIChatbotSettingsTabs";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { LoadingSpinner } from "@velaro/velaro-component-lib";

// ── Types ─────────────────────────────────────────────────────────────────────

interface KnowledgeOverride {
  id: number;
  topic: string;
  overrideFact: string;
  searchTerms: string;
  priority: number;
  expiresAt: string | null;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

const EMPTY_FORM = {
  topic: "",
  overrideFact: "",
  searchTerms: "",
  priority: 0,
  expiresAt: "",
  isActive: true
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function AIChatbotSettingsKnowledgeOverrides() {
  const { id } = useParams();
  const api = useApi();
  const toastAlert = useToastAlert();

  const [overrides, setOverrides] = useState<KnowledgeOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state — null means drawer is closed
  const [editTarget, setEditTarget] = useState<KnowledgeOverride | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Data loading ────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    try {
      const res = await api.admin.get("KnowledgeOverride");
      const data = await res.json();
      setOverrides(Array.isArray(data) ? data : []);
    } catch {
      setOverrides([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Form helpers ────────────────────────────────────────────────────────────

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(o: KnowledgeOverride) {
    setEditTarget(o);
    setForm({
      topic: o.topic,
      overrideFact: o.overrideFact,
      searchTerms: o.searchTerms ?? "",
      priority: o.priority,
      expiresAt: o.expiresAt ? o.expiresAt.split("T")[0] : "",
      isActive: o.isActive
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
  }

  // ── API actions ─────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!form.topic.trim()) {
      toastAlert.displayToast(Severity.Error, "Topic is required.");
      return;
    }
    if (!form.overrideFact.trim()) {
      toastAlert.displayToast(Severity.Error, "Fact text is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        topic: form.topic.trim(),
        overrideFact: form.overrideFact.trim(),
        searchTerms: form.searchTerms.trim() || null,
        priority: Number(form.priority) || 0,
        expiresAt: form.expiresAt ? form.expiresAt : null,
        isActive: form.isActive
      };

      let res;
      if (editTarget) {
        res = await api.admin.put(`KnowledgeOverride/${editTarget.id}`, payload);
      } else {
        res = await api.admin.post("KnowledgeOverride", payload);
      }

      const data = await res.json();
      if (data?.success) {
        toastAlert.displayToast(
          Severity.Success,
          editTarget ? "Override updated." : "Override created."
        );
        closeForm();
        await load();
      } else {
        toastAlert.displayToast(
          Severity.Error,
          data?.message ?? "Save failed — please try again."
        );
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Save failed — see browser console.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(o: KnowledgeOverride) {
    if (!window.confirm(`Delete override "${o.topic}"? This cannot be undone.`)) return;

    try {
      const res = await api.admin.delete(`KnowledgeOverride/${o.id}`);
      const data = await res.json();
      if (data?.success) {
        toastAlert.displayToast(Severity.Success, "Override deleted.");
        await load();
      } else {
        toastAlert.displayToast(Severity.Error, data?.message ?? "Delete failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Delete failed — see browser console.");
    }
  }

  async function handleToggle(o: KnowledgeOverride) {
    try {
      const res = await api.admin.patch(`KnowledgeOverride/${o.id}/toggle`, {});
      const data = await res.json();
      if (data?.success) {
        setOverrides((prev) =>
          prev.map((x) => (x.id === o.id ? { ...x, isActive: data.isActive } : x))
        );
      } else {
        toastAlert.displayToast(Severity.Error, "Toggle failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Toggle failed.");
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col">
      <AIChatbotSettingsTabs selectedTab="Knowledge Overrides" aiConfigId={id!} />

      <div className="max-w-4xl mx-auto w-full px-4 pb-10">

        {/* Page header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Knowledge Overrides</h2>
            <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
              Define authoritative facts that are always injected into the AI prompt — before vector
              search results. Use these to guarantee the AI uses your exact policy text for
              brand-critical topics like returns, warranties, pricing, or holiday hours.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap ml-4"
          >
            + Add Override
          </button>
        </div>

        {/* How it works callout */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800">
          <strong>How it works:</strong> When a customer asks a question, the AI receives your
          active overrides first — before searching the knowledge base. Facts without trigger
          keywords are always injected. Facts with trigger keywords (e.g.{" "}
          <code className="bg-blue-100 px-1 rounded">warranty, return</code>) are only injected
          when the customer&apos;s message contains one of those words.
        </div>

        {/* Empty state */}
        {overrides.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">
            <div className="text-3xl mb-2">📋</div>
            <div className="font-medium mb-1">No overrides yet</div>
            <div className="text-sm">
              Add your first override to guarantee exact answers for your most important policies.
            </div>
          </div>
        )}

        {/* Overrides list */}
        {overrides.length > 0 && (
          <div className="flex flex-col gap-3">
            {overrides.map((o) => (
              <div
                key={o.id}
                className={`bg-white border rounded-xl px-5 py-4 shadow-sm flex flex-col gap-2 ${
                  o.isActive ? "border-slate-200" : "border-slate-100 opacity-60"
                }`}
              >
                {/* Row 1: topic + badges + actions */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800">{o.topic}</span>
                    {o.priority > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        Priority {o.priority}
                      </span>
                    )}
                    {o.searchTerms && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        Keywords: {o.searchTerms}
                      </span>
                    )}
                    {o.expiresAt && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        Expires {new Date(o.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                    {o.usageCount > 0 && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {o.usageCount.toLocaleString()} uses
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Active toggle */}
                    <button
                      onClick={() => handleToggle(o)}
                      aria-label={o.isActive ? "Deactivate override" : "Activate override"}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                        o.isActive ? "bg-blue-600" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          o.isActive ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => openEdit(o)}
                      className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(o)}
                      className="text-sm text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      aria-label={`Delete override ${o.topic}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Row 2: fact preview */}
                <div className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100 whitespace-pre-wrap">
                  {o.overrideFact}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Slide-over form drawer ──────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="flex-1 bg-black/30"
            onClick={closeForm}
            aria-hidden="true"
          />

          {/* Drawer panel */}
          <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-slate-800">
                {editTarget ? "Edit Override" : "New Override"}
              </h3>
              <button
                onClick={closeForm}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                aria-label="Close drawer"
              >
                ×
              </button>
            </div>

            <div className="flex flex-col gap-5 px-6 py-6 flex-1">

              {/* Topic */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Topic <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.topic}
                  onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                  placeholder="e.g. Return Policy, Holiday Hours, Warranty"
                  maxLength={200}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p className="text-xs text-slate-400 mt-1">Short label shown in the AI prompt header.</p>
              </div>

              {/* Override Fact */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Override Fact <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.overrideFact}
                  onChange={(e) => setForm((f) => ({ ...f, overrideFact: e.target.value }))}
                  placeholder="Enter the exact text the AI should use when answering questions about this topic..."
                  maxLength={2000}
                  rows={5}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {form.overrideFact.length}/2000 chars. This text is injected verbatim into the AI
                  system prompt — be precise and concise.
                </p>
              </div>

              {/* Search Terms */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Trigger Keywords{" "}
                  <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.searchTerms}
                  onChange={(e) => setForm((f) => ({ ...f, searchTerms: e.target.value }))}
                  placeholder="e.g. warranty, return, refund, damaged"
                  maxLength={500}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Comma-separated. This fact is only injected when the customer&apos;s message
                  contains at least one keyword. Leave blank to always inject (global fact).
                </p>
              </div>

              {/* Priority + Expires grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <input
                    type="number"
                    min={0}
                    max={999}
                    value={form.priority}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))
                    }
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <p className="text-xs text-slate-400 mt-1">Lower = higher priority. Default: 0.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Expires On{" "}
                    <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={form.expiresAt}
                    onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <p className="text-xs text-slate-400 mt-1">Auto-deactivates after this date.</p>
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                  aria-label={form.isActive ? "Deactivate" : "Activate"}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    form.isActive ? "bg-blue-600" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      form.isActive ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-sm text-slate-700">
                  {form.isActive ? "Active — will be injected into AI prompts" : "Inactive — will be skipped"}
                </span>
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t flex gap-3 justify-end">
              <button
                onClick={closeForm}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? "Saving…" : editTarget ? "Save Changes" : "Create Override"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
